<template>
  <div class="px-3 py-1.5 flex border border-brand-gray-300 bg-brand-gray-150">
    <div class="flex flex-grow gap-x-4 items-center">
      <faTriangleExclamation
        v-if="data.notificationParameters.alertType == 'YELLOW_CODE'"
        class="size-7 fill-yellow-500"
      />
      <faCircleTimes
        v-if="data.notificationParameters.alertType == 'RED_CODE'"
        class="size-7 fill-red-500"
      />
      <faCircleCheck
        v-if="data.notificationParameters.alertType == 'GREEN_CODE'"
        class="size-7 fill-green-500"
      />
      <span class="flex-1 max-w-prose text-sm font-medium">{{ data.body }}</span>
    </div>
    <div class="flex-shrink flex gap-x-4 justify-end items-center gap-y-2">
      <div class="flex flex-col gap-y-0 5">
        <small class="text-xs ltr:font-sans">{{ $t('alertcreationDate') }}</small>
        <span class="font-medium">{{ formatDate(data.creationDate) }}</span>
      </div>
      <div class="flex flex-col gap-y-0 5">
        <small class="text-xs ltr:font-sans">{{ $t('alertFileexpiryDate') }}</small>
        <span class="font-medium">
          {{ formatDate(data.notificationParameters.expiryDate) }}
        </span>
      </div>
      <tippy
        v-if="(data.actions ?? []).length > 0"
        :theme="useThemeStore().currentScheme == 'light' ? 'light-border' : 'dark-border'"
        :arrow="false"
        :offset="[0, 4]"
        interactive
        trigger="click"
        v-bind="{ ...(maxDate && { maxDate: new Date() }) }"
        placement="bottom-end"
      >
        <button class="rounded p-2 bg-gray-600" @click.prevent="">
          <faBars class="w-3 fill-white" />
        </button>
        <template #content>
          <div class="flex flex-col gap-y-2 max-h-[12rem] overflow-y-auto">
            <button
              :key="key"
              v-for="(actionItem, key) in data.actions"
              class="text-start px-3 py-1.5 text-sm text-brand-black-secondary hover:text-primary-700 hover:bg-gray-200"
              @click="onServiceClick(data, actionItem)"
            >
              {{
                actionItem.serviceDescription.childTransactionDescription ||
                actionItem.serviceDescription.transactionDescription
              }}
            </button>
          </div>
        </template>
      </tippy>
    </div>
  </div>
</template>

<script setup>
import faBars from '@/assets/icons/faBars.svg'
import faTriangleExclamation from '@/assets/icons/faTriangleExclamation.svg'
import faCircleTimes from '@/assets/icons/faCircleTimes.svg'
import faCircleCheck from '@/assets/icons/faCircleCheck.svg'
import { DateTime } from 'luxon'
import { useThemeStore } from '@/stores/theme'
import { useGlobalStore } from '@/stores/global'
import { useAuthStore } from '@/stores/auth'
import client_lookups from '@/assets/json/clientLookups.json'
import { useRouter } from 'vue-router'
import { onMounted } from 'vue'
import client_config from '@/assets/json/client_config.json'

defineProps({
  data: {
    type: Object,
    required: true
  }
})

const {
  application: { establishment },
  alertTemplateType: {
    alertExpiredResidency,
    alertExpiredVisa,
    alertExpiredEID,
    alertExpiredPassport,
    alertResidenceModification
  },
  adminRegions: { abuDhabi }
} = client_lookups

const { v2ServiceFormTransactionIds } = client_config

const router = useRouter()
const globalStore = useGlobalStore()
const authStore = useAuthStore()

onMounted(() => {
  console.log('authStore.user', authStore.user)
})

const formatDate = (date) => DateTime.fromJSDate(new Date(date)).toFormat('dd-MM-yyyy')

const onServiceClick = async (data, actionItem) => {
  const { notificationParameters } = data

  const {
    serviceDescription: { serviceTransId }
  } = actionItem

  const { serviceConfig } = await globalStore.fetchConfig(serviceTransId)

  if (!serviceConfig) {
    await authStore.logout()
    return
  }

  if (authStore.user.applicationId == establishment) {
    if (
      [alertExpiredResidency, alertExpiredVisa, alertExpiredEID].includes(
        notificationParameters.templateTypeId
      )
    ) {
      router.push()
    }
    return
  }

  if ([alertExpiredResidency, alertExpiredVisa].includes(notificationParameters.templateTypeId)) {
    // processIndividualVisaAndResi
    const { secondaryRequestUrl } = serviceConfig

    if (secondaryRequestUrl && v2ServiceFormTransactionIds.includes(+serviceTransId)) {
      globalStore.showConfirmDialog({
        title: 'newForm',
        message: 'would_you_like_to_try_our_new_form'
      })

      // globalStore.$subscribe((mutation, state) => {
      //   if (state.confirmResponse) {
      //     router.push({
      //       name: 'ServiceForm',
      //       params: {
      //         serviceId: serviceTransId
      //       },
      //       query: {
      //         fileNumber,
      //         sponsoredUN,
      //         administrativeRegionId,
      //         withException,
      //         isCanceled
      //       }
      //     })
      //   }
      // })
      /**
       * TODO: show confirm dialog that asks whether to use
       * the old or the new form
       * but our system doesn't support different paths.
       */
    } else {
      const {
        fileNumber,
        unifiedNumber: sponsoredUN,
        administrativeRegionId,
        withException = false,
        isCanceled = 0
      } = notificationParameters

      router.push({
        name: 'ServiceForm',
        params: {
          serviceId: serviceTransId
        },
        query: {
          fileNumber,
          sponsoredUN,
          administrativeRegionId,
          withException,
          isCanceled
        }
      })
    }
  } else if ([alertExpiredEID].includes(notificationParameters.templateTypeId)) {
    // processIndividualEID
    const { administrativeRegionId, unifiedNumber, fileNumber } = notificationParameters
    router.push({
      name: 'ServiceForm',
      params: {
        serviceId: serviceTransId
      },
      query: {
        fileNumber,
        administrativeRegionId: abuDhabi,
        withException: false,
        personUN: unifiedNumber
      }
    })
  } else if ([alertExpiredPassport].includes(notificationParameters.templateTypeId)) {
    // const { unifiedNumber } = notificationParameters
    // processCitizenPassport
    // go to manageCitizenFamily
    /**
     * router.push(
     *   {
     *     name: manageCitizenFamily,
     *     query: {
     *       transactionId: serviceTransId,
     *       unifiedNumber
     *     }
     *   },
     * )
     */
  } else if ([alertResidenceModification].includes(notificationParameters.templateTypeId)) {
    const {
      fileNumber,
      unifiedNumber,
      administrativeRegionId,
      withException = false,
      isCanceled = 0
    } = notificationParameters

    router.push({
      name: 'ServiceForm',
      params: {
        serviceId: serviceTransId
      },
      query: {
        fileNumber,
        unifiedNumber,
        administrativeRegionId,
        withException,
        isCanceled
      }
    })
  }
}
</script>

<style lang="scss" scoped></style>
